/**
 * ClientSummaryMetrics — o bloco de indicadores do Resumo do Cliente.
 *
 * Só apresentação: toda a conta vem pronta de `computeClientMetrics`. Duas
 * regras de honestidade que este componente precisa manter:
 *
 *  - `null` NUNCA vira zero. Ticket médio e conversão sem base viram "—", não
 *    "R$ 0,00" / "0%": o cliente não converteu 0% das propostas, ele não teve
 *    proposta nenhuma.
 *  - O bloco financeiro só existe quando `metrics.finance` existe. Para um
 *    `member`, `finance_entries` volta vazio pela RLS (owner/admin-only), então
 *    mostrar "R$ 0,00 a receber" seria uma informação falsa — e assinar o PRO
 *    não resolveria, porque o gate dele é de PAPEL, não de plano.
 */
import { Card, CardContent } from '@/components/ui/card';
import {
  TrendingUp,
  Receipt,
  Percent,
  CalendarClock,
  FileClock,
  Wallet,
  AlertTriangle,
} from 'lucide-react';
import type { ClientMetrics } from '@/lib/clientSummary';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (date: Date) =>
  date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

/** "—" é o vazio honesto: distingue "não há base para a conta" de "deu zero". */
const EMPTY = '—';

interface StatProps {
  label: string;
  value: string;
  hint?: string;
  Icon: typeof TrendingUp;
  tone?: 'default' | 'warning' | 'danger';
}

function Stat({ label, value, hint, Icon, tone = 'default' }: StatProps) {
  const toneClass =
    tone === 'danger'
      ? 'text-destructive'
      : tone === 'warning'
      ? 'text-amber-600 dark:text-amber-500'
      : 'text-foreground';

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon className="h-3.5 w-3.5 shrink-0" />
          <p className="truncate text-[11px] uppercase tracking-wide">{label}</p>
        </div>
        <p className={`mt-1 text-lg font-bold ${toneClass}`}>{value}</p>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

interface ClientSummaryMetricsProps {
  metrics: ClientMetrics;
}

export function ClientSummaryMetrics({ metrics }: ClientSummaryMetricsProps) {
  const {
    lifetimeValue,
    closedCount,
    averageTicket,
    conversionRate,
    approvedProposalsCount,
    proposalsCount,
    openValue,
    openCount,
    lastInteraction,
    finance,
  } = metrics;

  return (
    <div className="space-y-3">
      {/* Destaque: o que este cliente já trouxe de fato. */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Valor total (LTV)
              </p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(lifetimeValue)}</p>
            </div>
            <TrendingUp className="h-8 w-8 shrink-0 text-primary/60" />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {closedCount} documento{closedCount !== 1 ? 's' : ''} fechado
            {closedCount !== 1 ? 's' : ''} (orçamentos aprovados e vendas)
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Stat
          label="Valor médio"
          Icon={Receipt}
          value={averageTicket === null ? EMPTY : formatCurrency(averageTicket)}
          hint={averageTicket === null ? 'Nenhum documento fechado' : undefined}
        />
        <Stat
          label="Conversão"
          Icon={Percent}
          value={conversionRate === null ? EMPTY : `${Math.round(conversionRate * 100)}%`}
          hint={
            conversionRate === null
              ? 'Nenhuma proposta enviada'
              : `${approvedProposalsCount} de ${proposalsCount} orçamento${
                  proposalsCount !== 1 ? 's' : ''
                }`
          }
        />
        <Stat
          label="Em aberto"
          Icon={FileClock}
          value={formatCurrency(openValue)}
          hint={`${openCount} orçamento${openCount !== 1 ? 's' : ''} pendente${
            openCount !== 1 ? 's' : ''
          }`}
          tone={openValue > 0 ? 'warning' : 'default'}
        />
        <Stat
          label="Última interação"
          Icon={CalendarClock}
          value={lastInteraction ? formatDate(lastInteraction) : EMPTY}
          hint={lastInteraction ? undefined : 'Sem documentos'}
        />
      </div>

      {/* Bloco financeiro — ausente de propósito para quem não pode lê-lo. */}
      {finance && (
        <div className="grid grid-cols-2 gap-3">
          <Stat
            label="A receber"
            Icon={Wallet}
            value={formatCurrency(finance.receivable)}
            hint="Em aberto, a vencer"
          />
          <Stat
            label="Vencido"
            Icon={AlertTriangle}
            value={formatCurrency(finance.overdue)}
            hint={`${finance.overdueCount} lançamento${
              finance.overdueCount !== 1 ? 's' : ''
            } em atraso`}
            tone={finance.overdue > 0 ? 'danger' : 'default'}
          />
        </div>
      )}
    </div>
  );
}
